import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Alert, Spinner, Button, Badge, Row, Col } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Model Catalog Browser Component
 * Allows admins to browse OpenRouter's live model catalog and add models to the allowlist
 */
const ModelCatalogBrowser = () => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('all'); // all, openai, anthropic, openrouter
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isStale, setIsStale] = useState(false);
  const [adding, setAdding] = useState(null);
  const [addMessage, setAddMessage] = useState('');

  useEffect(() => {
    fetchCatalog();
  }, []);

  useEffect(() => {
    filterModels(searchTerm, providerFilter);
  }, [models, searchTerm, providerFilter]);

  const fetchCatalog = async (refresh = false) => {
    setLoading(true);
    setError('');
    setIsStale(false);
    try {
      const url = `/api/admin/tools/openrouter-catalog${refresh ? '?refresh=1' : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setModels(data.data || []);
        if (data.stale) {
          setIsStale(true);
          setError(data.error);
        }
      }
    } catch (err) {
      setError('Failed to fetch catalog');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSourceLabel = (directProvider) => {
    if (directProvider === 'openai') return 'OpenAI';
    if (directProvider === 'anthropic') return 'Anthropic';
    return 'OpenRouter';
  };

  const filterModels = (term, provider) => {
    let result = models;

    // Filter by provider
    if (provider !== 'all') {
      result = result.filter(m => {
        if (provider === 'openai') return m.directProvider === 'openai';
        if (provider === 'anthropic') return m.directProvider === 'anthropic';
        if (provider === 'openrouter') return m.directProvider === null;
        return true;
      });
    }

    // Filter by search term
    if (term) {
      const lower = term.toLowerCase();
      result = result.filter(m =>
        m.model_id.toLowerCase().includes(lower) ||
        m.name.toLowerCase().includes(lower)
      );
    }

    setFilteredModels(result);
  };

  const handleAddModel = async (model) => {
    setAdding(model.model_id);
    setAddMessage('');

    try {
      // Determine provider and model_id based on directProvider
      const isDirectProvider = model.directProvider !== null && model.directProvider !== undefined;
      const payload = {
        provider: isDirectProvider ? model.directProvider : 'openrouter',
        model_id: isDirectProvider ? model.directModelId : model.model_id,
        model_name: model.name,
        // modality is a compound string like "text+image->text+image"; classify by the OUTPUT side.
        // (endpoint accepts text/tts/image/video only - map audio output to tts.)
        model_type: (() => {
          const out = String(model.modality || '').split('->').pop() || '';
          if (out.includes('image')) return 'image';
          if (out.includes('video')) return 'video';
          if (out.includes('audio')) return 'tts';
          return 'text';
        })(),
        input_cost_per_million: model.inputPerMillion,
        output_cost_per_million: model.outputPerMillion,
        is_active: true
      };

      const response = await fetch('/api/admin/models', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        // Check for specific error messages (e.g., duplicate)
        const errorText = result.error || 'Failed to add model';
        setAddMessage({
          type: 'danger',
          text: errorText
        });
      } else {
        const source = getSourceLabel(model.directProvider);
        setAddMessage({
          type: 'success',
          text: `Model "${model.name}" (${source}) added to allowlist`
        });
        // Optionally refetch after a delay to update state
        setTimeout(() => {
          fetchCatalog();
        }, 2000);
      }
    } catch (err) {
      setAddMessage({
        type: 'danger',
        text: 'Error adding model: ' + err.message
      });
      console.error(err);
    } finally {
      setAdding(null);
    }
  };

  const formatRate = (rate) => {
    return rate ? `$${(rate / 1e6).toFixed(6)}/token` : 'N/A';
  };

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header>
          <h5 className="mb-0">OpenRouter Model Catalog</h5>
        </Card.Header>
        <Card.Body>
          {isStale && (
            <Alert variant="warning" dismissible>
              Showing cached data - last fetch failed. Live pricing may be outdated.
            </Alert>
          )}

          {error && !isStale && (
            <Alert variant="danger" dismissible>
              {error}
            </Alert>
          )}

          {addMessage && (
            <Alert
              variant={addMessage.type}
              dismissible
              onClose={() => setAddMessage('')}
            >
              {addMessage.text}
            </Alert>
          )}

          {/* Search and Filter Controls */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Search Models</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by model ID or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Provider</Form.Label>
                <Form.Select
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                >
                  <option value="all">All Providers</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="openrouter">OpenRouter</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                onClick={() => fetchCatalog(true)}
                disabled={loading}
                className="w-100"
              >
                Refresh Catalog
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : filteredModels.length === 0 ? (
            <p className="text-muted">
              {searchTerm ? 'No models match your search.' : 'No models available.'}
            </p>
          ) : (
            <>
              <p className="text-muted small">
                Showing {filteredModels.length} of {models.length} models
              </p>
              <div style={{ overflowX: 'auto' }}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Model ID</th>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Modality</th>
                      <th>Context Window</th>
                      <th>Input / Output Price</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModels.map((model) => {
                      const source = getSourceLabel(model.directProvider);
                      const isDirectProvider = model.directProvider !== null && model.directProvider !== undefined;

                      return (
                        <tr key={model.model_id}>
                          <td>
                            <div>
                              <code style={{ fontSize: '0.85rem' }}>
                                {model.model_id}
                              </code>
                              {isDirectProvider && (
                                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '4px' }}>
                                  Direct adapter (OpenRouter pricing ~5% high)
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{model.name}</td>
                          <td>
                            <Badge
                              bg={
                                model.directProvider === 'openai'
                                  ? 'primary'
                                  : model.directProvider === 'anthropic'
                                  ? 'secondary'
                                  : 'warning'
                              }
                            >
                              {source}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info">
                              {model.modality}
                            </Badge>
                          </td>
                          <td>
                            {model.contextWindow.toLocaleString()}
                          </td>
                          <td>
                            {formatRate(model.inputPerMillion)} / {formatRate(model.outputPerMillion)}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleAddModel(model)}
                              disabled={adding === model.model_id}
                            >
                              {adding === model.model_id ? (
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                              ) : (
                                'Add'
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </AdminToolsLayout>
  );
};

export default ModelCatalogBrowser;
